import classNames from 'classnames'
// @ts-ignore
import { DomElement } from 'htmlparser2'
import Link from 'next/link'
import Script from 'next/script'
import { Fragment, useEffect, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import BSCard from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser'
import LazyLoad from 'react-lazyload'

import Card from '@/components/Card/Card'
import CardHover from '@/components/CardHover/CardHover'
import Container from '@/components/Container/Container'
import { ExplicitHead } from '@/components/DefaultHead/DefaultHead'
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary'
import Main from '@/components/Main/Main'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import AuthorBio from '@/components/Wordpress/AuthorBio/AuthorBio'
import Deck from '@/components/Wordpress/Deck/Deck'
import { PostType } from '@/types/wordpress'
import useDirectories from '@/utils/useDirectories'

import Author from '../ArticlePreview/Author'
import Date from '../ArticlePreview/Date'
import FeaturedImage from '../ArticlePreview/FeaturedImage'
import CSBEmbed from '../CSBEmbed'
import Disqus from '../Disqus'
import ReadMorePreview from '../ReadMorePreview'

import styles from './ArticlePage.module.scss'
import Errors from './Errors'

export const createTransform = ({
  cardNamesRef,
  errorsRef,
  isClient,
  isPreview,
  keyRef,
  slug,
  title,
}: {
  cardNamesRef: React.MutableRefObject<string[]>
  errorsRef: React.MutableRefObject<string[]>
  isClient: boolean
  isPreview: boolean
  keyRef: React.MutableRefObject<number>
  slug: string
  title: string
}) => {
  const _key = () => `${keyRef.current++}`

  const parseName = (name: string | undefined) => {
    if (!name) return ''
    const parsedName = name.replace('★', 'star').replace('†', 'dagger').replace('Φ', 'phi')
    if (isPreview) cardNamesRef.current.push(parsedName)
    return parsedName
  }

  const transform = (node: DomElement, i: number) => {
    const classList = node.attribs?.class?.split(' ') || []

    // [ec]
    if (classList.includes('edhrecp__cards')) {
      const key = _key()
      const children = node.children
        .filter(
          (child: DomElement) =>
            child.type === 'tag' && child.name === 'div' && child.attribs?.class?.includes('edhrecp__card-container'),
        )
        .map((child: DomElement, j: number) => {
          const name = parseName(child.attribs?.name)
          return (
            <Card
              isPreview={isPreview}
              key={`ec-${key}-${j}`}
              name={name}
              onlyBack={classList.includes('only-back')}
              onlyFront={classList.includes('only-front')}
              reverse={classList.includes('reverse')}
              sideBySide={classList.includes('side-by-side')}
              target='_blank'
            />
          )
        })
      return (
        <div className={styles.cards} key={`ec-${key}`}>
          {children}
        </div>
      )
    }

    // [el]
    else if (classList.includes('edhrecp__link')) {
      const key = _key()
      const children = node.children
        .filter(
          (child: DomElement) =>
            child.type === 'tag' && child.name === 'a' && child.attribs?.class === 'edhrecp__link-a',
        )
        .map((child: DomElement, j: number) => {
          const name = parseName(child.attribs?.content)
          return (
            <CardHover
              isPreview={isPreview}
              key={`el-${key}-${j}`}
              name={name}
              onlyBack={classList.includes('only-back')}
              onlyFront={classList.includes('only-front')}
              reverse={classList.includes('reverse')}
              sideBySide={classList.includes('side-by-side')}
              target='_blank'
            >
              {node.children[0].children[0]?.data}
            </CardHover>
          )
        })
      return (
        <span className={node.attribs?.class} key={`el-${key}`}>
          {children}
        </span>
      )
    }

    // [ed]
    else if (classList.includes('edhrecp__deck')) {
      const key = _key()
      const children = node.children
        .filter(
          (child: DomElement) =>
            child.type === 'tag' && child.name === 'span' && child.attribs?.class === 'edhrecp__deck-s',
        )
        .map((child: DomElement, j: number) => {
          const deckKey = `ed-${key}-${j}`
          const deckTitle = child?.attribs.name
          if (isClient && title.length + deckTitle.length >= 125) {
            errorsRef.current.push(
              `Generated Archidekt deck title too long.  Please shorten the deck title, or the post title.`,
            )
          }
          return (
            <ErrorBoundary key={deckKey} tag={`${slug}-${deckKey}`}>
              <Deck
                archidekt_link={child?.attribs?.archidekt_link}
                rawDeck={child.attribs?.cards}
                slug={slug}
                title={deckTitle}
              />
            </ErrorBoundary>
          )
        })
      return (
        <div className={node.attribs?.class} key={`ed-${key}`}>
          {children}
        </div>
      )
    }

    // [et]
    else if (classList.includes('edhrecp__table')) {
      const key = _key()
      return (
        <Table bordered className={styles.table} hover key={`et-${key}`} striped>
          <tbody>
            {node.children.map((child: DomElement, j: number) => convertNodeToElement(child, j, transform))}
          </tbody>
        </Table>
      )
    }

    // [mana]
    else if (classList.includes('mana-container')) {
      const key = _key()
      return (
        <span className={styles.mana} key={`mana-${key}`}>
          {convertNodeToElement(node.children[0], i, transform)}
        </span>
      )
    }

    // [efn_note]
    else if (classList.includes('easy-footnote')) {
      const key = _key()
      return (
        <span className={styles.efnNote} key={`efn-${key}`}>
          <OverlayTrigger
            overlay={
              <Tooltip id={`tooltip-efn-${key}`}>
                <span dangerouslySetInnerHTML={{ __html: node.children[0]?.attribs?.title }} />
              </Tooltip>
            }
            placement='bottom'
          >
            <span>{convertNodeToElement(node.children[0].children[0].children[0], i, transform)}</span>
          </OverlayTrigger>
        </span>
      )
    }

    // WP embeds
    else if (node.type === 'tag' && node.attribs?.class?.includes('wp-embedded-content')) {
      if (node.name === 'blockquote') {
        const match = node?.children[0]?.children[0].attribs.href
        if (!match || !match.startsWith('https://edhrec.com')) return convertNodeToElement(node, i, transform)
        return (
          <LazyLoad key={`embed-${_key()}`}>
            <ReadMorePreview slug={match.split('/')[4]} />
          </LazyLoad>
        )
      } else if (node.name === 'iframe') {
        return null
      }
    }

    // WP embeds (if previous format disabled by plugin)
    else if (node.type === 'tag' && node.name === 'p') {
      const match = node.children[0]?.data?.trim()
      if (!match || !match.startsWith('https://edhrec.com')) return convertNodeToElement(node, i, transform)
      return (
        <p>
          <LazyLoad key={`embed-${_key()}`}>
            <ReadMorePreview slug={match.split('/')[4]} />
          </LazyLoad>
        </p>
      )
    }

    // Misc links
    else if (node.type === 'tag' && node.name === 'a') {
      const key = _key()

      // Anchor target
      if (node.attribs?.id) {
        return (
          <a id={node.attribs.id} key={`a-${key}`}>
            {node.children[0]?.data}
          </a>
        )
      }

      // Anchor link or mailto
      if (!node.attribs.href?.startsWith('http')) {
        return (
          <a href={node.attribs.href} key={`a-${key}`}>
            {node.children[0]?.data}
          </a>
        )
      }

      return (
        <OutboundLink key={`a-${key}`} url={node.attribs.href}>
          {node.children.map((child: DomElement, j: number) => (
            <Fragment key={`a-${key}-${j}`}>{convertNodeToElement(child, j, transform)}</Fragment>
          ))}
        </OutboundLink>
      )
    }

    // <noscript> (iframes)
    else if (node.name === 'noscript') {
      return node.children.map((child: DomElement) => convertNodeToElement(child, i, transform))
    }

    // <script>
    else if (node.type === 'script' && node.name === 'script' && isClient) {
      const key = _key()
      if (node.attribs.src) {
        if (node.attribs.src.startsWith('https://commanderspellbook.com/embed.js?v=1')) {
          return <CSBEmbed id={node.parent.attribs.id} key={`s-${key}`} url={node.attribs.src} />
        } else if (node.attribs.src.startsWith('https://secure.polldaddy.com/p/')) {
          const match = node.attribs.src.match(/https:\/\/secure.polldaddy.com\/p\/(\d+)\.js/)
          return (
            <Fragment key={`s-${key}`}>
              <Script src={node.attribs.src} />
              <div className={styles.crowdsignal} id={`PDI_container${match[1]}`} />
            </Fragment>
          )
        } else {
          return <Script key={`s-${key}`} src={node.attribs.src} />
        }
      } else if (node.children.length) {
        return (
          <Script id={`s-${key}`} key={`s-${key}`}>
            {node.children[0].data}
          </Script>
        )
      } else {
        return (
          <script key={`s-${key}`} {...node.attribs}>
            {node.children.map((child: DomElement, j: number) => (
              <Fragment key={`s-${key}-${j}`}>{convertNodeToElement(child, j, transform)}</Fragment>
            ))}
          </script>
        )
      }
    } else if (node.type === 'tag' && node.name === 'p') {
      if (node.children.length === 0) {
        return null
      }
    } else if (node.type === 'text' && node.parent === null) {
    }
  }

  return transform
}

type Props = { enableAdhesion: boolean; initCountry?: string; post: PostType }

const ArticlePage = ({
  enableAdhesion,
  post: { author, content, databaseId, date, excerpt, featuredImageUrl, slug, tags, title },
}: Props) => {
  const { subpage } = useDirectories()
  const cardNamesRef = useRef<string[]>([])
  const errorsRef = useRef<string[]>([])
  const keyRef = useRef<number>(0)
  const [isClient, setIsClient] = useState(false)

  const isPreview = subpage === 'preview'
  const transform = createTransform({
    cardNamesRef,
    errorsRef,
    isClient,
    isPreview,
    keyRef,
    slug,
    title,
  })

  useEffect(() => {
    if (!isClient) setIsClient(true)
  }, [])

  return (
    <Main>
      <Container article>
        <ExplicitHead
          description={excerpt}
          imageUrl={featuredImageUrl}
          keywords={tags?.map((tag) => tag.name).join(', ')}
          title={title}
        />
        {/* eslint-disable-next-line @next/next/no-css-tags*/}
        <link
          href='//cdn.jsdelivr.net/npm/mana-font@latest/css/mana.min.css'
          id='mana-font'
          rel='stylesheet'
          type='text/css'
        />
        <div className='d-flex flex-column gap-2'>
          {isPreview && <Errors cardNames={cardNamesRef.current} errors={errorsRef.current} />}
          {title && <h2 dangerouslySetInnerHTML={{ __html: title }} />}
          {(author || date) && (
            <div className='align-items-center d-flex gap-2'>
              {date && <Date dateString={date} />}
              <div className='text-secondary'>by</div>
              {author && <Author author={author} />}
            </div>
          )}
          {featuredImageUrl && <FeaturedImage height='auto' src={featuredImageUrl} title={title} width='auto' />}
        </div>
        <div className={classNames(styles.content, 'wp-content')}>{ReactHtmlParser(content, { transform })}</div>
        {author && <AuthorBio author={author} />}
        <BSCard className={classNames(styles.cta, 'shadow-sm')}>
          <BSCard.Body>
            <BSCard.Text>
              <BSCard.Title>Want more Commander content, right in your inbox?</BSCard.Title>
              <div className='mb-2 mt-3'>
                To stay on top of all our news, features, and deck techs, sign up for our EDHRECap e-mail newsletter.
              </div>
              <div className='align-items-center d-flex gap-2 justify-content-center'>
                <Link className='btn btn-primary mt-2' href='/newsletter'>
                  Sign up for the EDHRECap!
                </Link>
              </div>
            </BSCard.Text>
          </BSCard.Body>
        </BSCard>
        {databaseId && (
          <>
            <Disqus identifier={databaseId} slug={slug} title={title} />
            <BSCard className={classNames(styles.codeOfConduct, 'shadow-sm')}>
              <BSCard.Body>
                <BSCard.Title>EDHREC Code of Conduct</BSCard.Title>
                <BSCard.Text>
                  Your opinions are welcome. We love hearing what you think about <i>Magic</i>! We ask that you are
                  always respectful when commenting. Please keep in mind how your comments could be interpreted by
                  others. Personal attacks on our writers or other commenters will not be tolerated. Your comments may
                  be removed if your language could be interpreted as aggressive or disrespectful. You may also be
                  banned from writing further comments.
                </BSCard.Text>
              </BSCard.Body>
            </BSCard>
          </>
        )}
        {enableAdhesion && <div className='mvHomeAdhesion' />}
      </Container>
    </Main>
  )
}

export default ArticlePage
